export interface CsvPaymentDraft {
	storeDisplay: string;
	totalAmount: number;
	transactedAt: string;
	categoryLabel: string | null;
	source: "csv";
	rowNumber: number;
	warnings: ("merchant" | "date" | "category")[];
}

export interface CsvImportResult {
	records: CsvPaymentDraft[];
	rejected: {
		rowNumber: number;
		reason: "amount_missing" | "income" | "transfer" | "empty" | "date";
	}[];
}

function parseRows(text: string): string[][] {
	const rows: string[][] = [];
	let row: string[] = [];
	let field = "";
	let quoted = false;
	for (let index = 0; index < text.length; index += 1) {
		const char = text[index];
		if (char === '"') {
			if (quoted && text[index + 1] === '"') {
				field += '"';
				index += 1;
			} else quoted = !quoted;
			continue;
		}
		if (char === "," && !quoted) {
			row.push(field);
			field = "";
			continue;
		}
		if ((char === "\n" || char === "\r") && !quoted) {
			if (char === "\r" && text[index + 1] === "\n") index += 1;
			row.push(field);
			if (row.some((value) => value.trim())) rows.push(row);
			row = [];
			field = "";
			continue;
		}
		field += char;
	}
	row.push(field);
	if (row.some((value) => value.trim())) rows.push(row);
	return rows;
}

function headerKey(value: string): string {
	return value
		.replace(/^\uFEFF/, "")
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[\s_./-]/g, "");
}

function findColumn(headers: string[], aliases: string[]): number {
	return headers.findIndex((header) => aliases.includes(header));
}

function parseAmount(value: string): number | null {
	const normalized = value.normalize("NFKC").trim();
	if (!normalized) return null;
	const digits = normalized.replace(/[^0-9.-]/g, "");
	const parsed = Number(digits);
	if (!Number.isFinite(parsed) || parsed === 0) return null;
	return Math.round(Math.abs(parsed));
}

function parseDate(
	value: string,
	now: Date,
): { iso: string; fallback: boolean } {
	const normalized = value.normalize("NFKC").trim();
	const match =
		/^(20\d{2})[./-](\d{1,2})[./-](\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?/.exec(
			normalized,
		);
	if (match) {
		const [, y = "", m = "", d = "", h = "12", min = "00"] = match;
		return {
			iso: `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h.padStart(2, "0")}:${min}:00+09:00`,
			fallback: false,
		};
	}
	const parsed = new Date(normalized);
	if (Number.isFinite(parsed.getTime()))
		return { iso: parsed.toISOString(), fallback: false };
	return { iso: now.toISOString(), fallback: true };
}

export function importPaymentCsv(
	text: string,
	now = new Date(),
): CsvImportResult {
	const rows = parseRows(text);
	if (rows.length < 2) return { records: [], rejected: [] };
	const headers = (rows[0] ?? []).map(headerKey);
	const dateIndex = findColumn(headers, [
		"날짜",
		"일자",
		"거래일",
		"거래일시",
		"transactedat",
		"date",
	]);
	const storeIndex = findColumn(headers, [
		"내용",
		"거래처",
		"사용처",
		"가맹점",
		"매장",
		"storedisplay",
		"merchant",
	]);
	const amountIndex = findColumn(headers, [
		"금액",
		"지출",
		"사용금액",
		"totalamount",
		"amount",
	]);
	const categoryIndex = findColumn(headers, [
		"분류",
		"대분류",
		"소분류",
		"카테고리",
		"categoryid",
		"category",
	]);
	const typeIndex = findColumn(headers, [
		"유형",
		"구분",
		"거래유형",
		"수입지출",
		"type",
	]);
	const records: CsvPaymentDraft[] = [];
	const rejected: CsvImportResult["rejected"] = [];
	for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
		const row = rows[rowIndex] ?? [];
		const rowNumber = rowIndex + 1;
		const type = typeIndex >= 0 ? (row[typeIndex] ?? "").trim() : "";
		if (/수입|income/i.test(type)) {
			rejected.push({ rowNumber, reason: "income" });
			continue;
		}
		if (/이체|transfer/i.test(type)) {
			rejected.push({ rowNumber, reason: "transfer" });
			continue;
		}
		const totalAmount =
			amountIndex >= 0 ? parseAmount(row[amountIndex] ?? "") : null;
		if (totalAmount === null) {
			rejected.push({ rowNumber, reason: "amount_missing" });
			continue;
		}
		const storeDisplay =
			storeIndex >= 0 ? (row[storeIndex] ?? "").trim().slice(0, 80) : "";
		const date = parseDate(dateIndex >= 0 ? (row[dateIndex] ?? "") : "", now);
		const categoryLabel =
			categoryIndex >= 0
				? (row[categoryIndex] ?? "").trim().slice(0, 60) || null
				: null;
		const warnings: CsvPaymentDraft["warnings"] = [];
		if (!storeDisplay) warnings.push("merchant");
		if (date.fallback) warnings.push("date");
		if (!categoryLabel) warnings.push("category");
		records.push({
			storeDisplay: storeDisplay || "확인 필요",
			totalAmount,
			transactedAt: date.iso,
			categoryLabel,
			source: "csv",
			rowNumber,
			warnings,
		});
	}
	return { records, rejected };
}

/* ---------- #46 다른 형식: 열을 직접 골라 가져오기 ---------- */

export interface CsvTable {
	headers: string[];
	rows: string[][];
}

export interface CsvColumnMapping {
	date: number;
	amount: number;
	store: number;
	category?: number | null;
}

/** Header row and data rows for the column picker and preview. */
export function readCsvTable(text: string): CsvTable {
	const rows = parseRows(text.replace(/^\uFEFF/, ""));
	return {
		headers: (rows[0] ?? []).map((value) => value.trim()),
		rows: rows.slice(1),
	};
}

/** Best guess of which column holds what, from common Korean/English names. */
export function guessCsvMapping(headers: string[]): CsvColumnMapping {
	const keys = headers.map(headerKey);
	const find = (pattern: RegExp) => keys.findIndex((key) => pattern.test(key));
	return {
		date: find(/날짜|일자|일시|거래일|date|time/),
		amount: find(/금액|지출|출금|amount|price/),
		store: find(/내용|매장|가맹점|사용처|거래처|적요|상호|store|merchant|memo/),
		category: (() => {
			const index = find(/분류|카테고리|category/);
			return index >= 0 ? index : null;
		})(),
	};
}

export function parseGenericCsv(
	text: string,
	mapping: CsvColumnMapping,
	now = new Date(),
): CsvImportResult {
	const table = readCsvTable(text);
	const records: CsvPaymentDraft[] = [];
	const rejected: CsvImportResult["rejected"] = [];
	if (mapping.date < 0 || mapping.amount < 0 || mapping.store < 0)
		return { records, rejected };
	table.rows.forEach((row, index) => {
		const rowNumber = index + 2;
		const totalAmount = parseAmount(row[mapping.amount] ?? "");
		if (totalAmount === null) {
			rejected.push({ rowNumber, reason: "amount_missing" });
			return;
		}
		const date = parseDate(row[mapping.date] ?? "", now);
		if (date.fallback) {
			rejected.push({ rowNumber, reason: "date" });
			return;
		}
		const storeDisplay = (row[mapping.store] ?? "").trim().slice(0, 80);
		const categoryLabel =
			mapping.category !== null && mapping.category !== undefined
				? (row[mapping.category] ?? "").trim().slice(0, 60) || null
				: null;
		const warnings: CsvPaymentDraft["warnings"] = [];
		if (!storeDisplay) warnings.push("merchant");
		if (!categoryLabel) warnings.push("category");
		records.push({
			storeDisplay: storeDisplay || "확인 필요",
			totalAmount,
			transactedAt: date.iso,
			categoryLabel,
			source: "csv",
			rowNumber,
			warnings,
		});
	});
	return { records, rejected };
}
